import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Repeat as RepeatIcon } from "lucide-react";
import { theme, card, btnPrimary } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { DimensionHeading, StatusTag } from "./Drawing";
import { PortalLink } from "./PortalLink";

// The same failure at several sites, or several organisations of a group (backend
// lib/repeatFindings.ts, routes/repeatFindings.routes.ts). The platform only suggests: one
// corrective action covering every place is raised when a person clicks, in this organisation's
// own register (the parent's, for a group). Sites and tags are filing; nothing here opens access.

type SignalKind = "site_gap" | "site_action" | "member_gap" | "member_finding" | "member_action";

const SIGNAL_LABELS: Record<SignalKind, string> = {
  site_gap: "Gap at the site",
  site_action: "Open corrective action",
  member_gap: "Gap in the Statement of Applicability",
  member_finding: "Auditor finding",
  member_action: "Open corrective action",
};

interface RepeatRow {
  controlId: string;
  controlLabel: string;
  frameworkId: string | null;
  severity: "high" | "medium";
  units: Array<{ id: string; name: string; signals: Array<{ kind: SignalKind; ref: string | null }> }>;
  covered: { id: string; displayId: number | null; status: string } | null;
}

interface RepeatResponse { repeats: RepeatRow[]; canRaise: boolean }

const ref = (n: number | null) => (n == null ? "the action" : `CA-${String(n).padStart(3, "0")}`);

function signalText(s: { kind: SignalKind; ref: string | null }) {
  const label = SIGNAL_LABELS[s.kind] ?? s.kind;
  if (!s.ref) return label;
  return s.kind === "member_finding" ? `${label} from ${s.ref}` : `${label} ${s.ref}`;
}

export function RepeatFindingsPanel({ scope, siteId }: { scope: "sites" | "group"; siteId?: string }) {
  const [data, setData] = useState<RepeatResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [params] = useSearchParams();
  const focus = params.get("repeat");
  const focusRef = useRef<HTMLDivElement | null>(null);

  const load = () => {
    api.get<RepeatResponse>(`/repeat-findings/${scope}`, siteId ? { siteId } : undefined)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load repeated findings")));
  };
  useEffect(load, [scope, siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data && focus) focusRef.current?.scrollIntoView({ block: "center" }); }, [data, focus]);

  const raise = async (controlId: string) => {
    setBusy(controlId);
    try {
      await api.post(`/repeat-findings/${scope}/actions`, { controlId });
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not raise the action"));
    } finally {
      setBusy(null);
    }
  };

  // Nothing repeated: a quiet panel on a site, nothing at all on the group page.
  if (!data && !error) return null;
  const repeats = data?.repeats ?? [];
  if (scope === "group" && repeats.length === 0 && !error) return null;

  const where = scope === "sites" ? "site" : "organisation";
  return (
    <div style={{ ...card, padding: "14px 18px", marginBottom: scope === "group" ? 20 : 0 }} id="repeats">
      <DimensionHeading as="h3" count={repeats.length} style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, margin: "0 0 4px" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <RepeatIcon size={14} />{scope === "sites" ? "Repeated at other sites" : "Repeated across the group"}
        </span>
      </DimensionHeading>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: theme.color.subtle, maxWidth: 720 }}>
        {scope === "sites"
          ? "Controls failing here and at other sites. One corrective action covering every site finds the shared cause once, instead of a local fix at each."
          : "Controls failing in more than one organisation of the group. One action in this organisation's register covers them all; each organisation still closes its own local work."}
      </p>
      {error && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
      {repeats.length === 0 && !error && (
        <div style={{ fontSize: 12, color: theme.color.faint, padding: "6px 0" }}>Nothing failing here is failing at another site.</div>
      )}
      {repeats.map((r) => {
        const focused = focus === r.controlId;
        return (
          <div
            key={r.controlId}
            ref={focused ? focusRef : undefined}
            style={{
              padding: "10px 0", borderTop: `1px solid ${theme.color.border}`, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap",
              ...(focused ? { boxShadow: `inset 3px 0 0 ${theme.color.accentText}`, paddingLeft: 10 } : {}),
            }}
          >
            <div style={{ flex: "1 1 360px", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{r.controlLabel}</span>
                <StatusTag tone={r.severity === "high" ? "gap" : "partial"}>{r.units.length} {where}s</StatusTag>
              </div>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: theme.color.text }}>
                {r.units.map((u) => (
                  <li key={u.id} style={{ margin: "2px 0" }}>
                    {scope === "sites" && u.id !== siteId
                      ? <PortalLink to={`/sites/${u.id}`} style={{ color: theme.color.accentText, textDecoration: "none" }}>{u.name}</PortalLink>
                      : <span style={{ fontWeight: u.id === siteId ? 600 : 400 }}>{u.name}{u.id === siteId ? " (this site)" : ""}</span>}
                    <span style={{ color: theme.color.subtle }}> · {u.signals.map(signalText).join(" · ")}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ flexShrink: 0 }}>
              {r.covered ? (
                <PortalLink to={`/improvement?action=${r.covered.id}`} style={{ fontSize: 12, color: theme.color.accentText, textDecoration: "none" }}>
                  Covered by {ref(r.covered.displayId)} →
                </PortalLink>
              ) : data?.canRaise ? (
                <button type="button" style={btnPrimary} disabled={busy === r.controlId} onClick={() => raise(r.controlId)}>
                  {busy === r.controlId ? "Raising…" : "Raise one action for all of these"}
                </button>
              ) : (
                <span style={{ fontSize: 12, color: theme.color.muted }}>Someone who can write controls can raise one action for these</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
